import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { corporateTheme } from '../themes/corporate';

const SummaryContainer = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 4rem 2rem;
`;

const Title = styled(motion.h2)`
  font-size: clamp(2.5rem, 5vw, 4rem);
  font-weight: ${corporateTheme.fontWeights.bold};
  color: ${corporateTheme.textPrimary};
  text-align: center;
  margin-bottom: 3rem;
  background: ${corporateTheme.primaryGradient};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-family: ${corporateTheme.fontFamily};
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  margin-bottom: 4rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const MainContent = styled(motion.div)`
  background: ${corporateTheme.cardBackground};
  backdrop-filter: blur(10px);
  border-radius: ${corporateTheme.borderRadius.xl};
  padding: 3rem;
  border: 1px solid ${corporateTheme.cardBorder};
  box-shadow: ${corporateTheme.cardShadow};
`;

const Highlight = styled(motion.div)`
  background: rgba(37, 99, 235, 0.05);
  backdrop-filter: blur(10px);
  border-radius: ${corporateTheme.borderRadius.xl};
  padding: 3rem;
  border: 1px solid rgba(37, 99, 235, 0.15);
  box-shadow: ${corporateTheme.cardShadow};
`;

const SectionTitle = styled.h3`
  font-size: 1.8rem;
  font-weight: ${corporateTheme.fontWeights.semibold};
  color: ${corporateTheme.primary};
  margin-bottom: 1.5rem;
  font-family: ${corporateTheme.fontFamily};
`;

const Text = styled.p`
  font-size: 1.1rem;
  line-height: 1.7;
  color: ${corporateTheme.textSecondary};
  margin-bottom: 1.5rem;
  font-family: ${corporateTheme.fontFamily};
`;

const KeyPoints = styled.ul`
  list-style: none;
  padding: 0;
`;

const KeyPoint = styled(motion.li)`
  display: flex;
  align-items: flex-start;
  margin-bottom: 1rem;
  font-size: 1.1rem;
  color: ${corporateTheme.textSecondary};
  font-family: ${corporateTheme.fontFamily};
  
  &:before {
    content: "→";
    color: ${corporateTheme.primary};
    font-weight: bold;
    margin-right: 1rem;
    margin-top: 0.2rem;
  }
`;

const StatsGrid = styled(motion.div)`
  display: grid;
  /* 4 cards: 2x2 layout for better balance */
  grid-template-columns: repeat(2, 1fr);
  gap: 2rem;
  margin-top: 3rem;
  
  @media (max-width: 1000px) {
    /* Switch to auto-fit for medium screens */
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  }
`;

const StatCard = styled(motion.div)`
  background: ${corporateTheme.cardBackground};
  backdrop-filter: blur(5px);
  border-radius: ${corporateTheme.borderRadius.lg};
  padding: 2rem;
  text-align: center;
  border: 1px solid ${corporateTheme.cardBorder};
  box-shadow: ${corporateTheme.cardShadow};
  position: relative;
  overflow: hidden;
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: ${corporateTheme.primaryGradient};
  }
`;

const StatNumber = styled.div`
  font-size: 3rem;
  font-weight: ${corporateTheme.fontWeights.bold};
  background: ${corporateTheme.primaryGradient};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 0.5rem;
  font-family: ${corporateTheme.fontFamily};
`;

const StatLabel = styled.div`
  font-size: 1rem;
  color: ${corporateTheme.textPrimary};
  font-weight: ${corporateTheme.fontWeights.medium};
  text-transform: uppercase;
  letter-spacing: 1px;
  font-family: ${corporateTheme.fontFamily};
`;

const StatDescription = styled.div`
  font-size: 0.9rem;
  color: ${corporateTheme.textSecondary};
  margin-top: 0.5rem;
  line-height: 1.4;
  font-family: ${corporateTheme.fontFamily};
`;

const ExecutiveSummary = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.8,
        ease: "easeOut"
      }
    }
  };

  const cardVariants = {
    hidden: { scale: 0.8, opacity: 0 },
    visible: {
      scale: 1,
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  return (
    <SummaryContainer ref={ref}>
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate={inView ? "visible" : "hidden"}
      >
        <Title variants={itemVariants}>
          Executive Summary
        </Title>

        <ContentGrid>
          <MainContent variants={itemVariants}>
            <SectionTitle>Our Vision</SectionTitle>
            <Text>
              GrowTech establishes a professional affiliate marketing program strategically 
              targeting the high-growth markets of Eastern Asia, including China, Japan, 
              South Korea, and Southeast Asian nations.
            </Text>
            <Text>
              With a strategic €12 million investment, we're building a robust technological 
              infrastructure and cultivating strong local partnerships, while making a 
              substantial €5 million investment into marketing for high-value content 
              website development focused on e-commerce, SaaS, and digital services.
            </Text>
            <KeyPoints>
              <KeyPoint variants={itemVariants}>
                First income streams commence after 6 months
              </KeyPoint>
              <KeyPoint variants={itemVariants}>
                Focus on data-driven strategies and regulatory compliance
              </KeyPoint>
              <KeyPoint variants={itemVariants}>
                Proprietary digital assets generating organic traffic
              </KeyPoint>
              <KeyPoint variants={itemVariants}>
                Transparent and high-performing affiliate ecosystem
              </KeyPoint>
            </KeyPoints>
          </MainContent>

          <Highlight variants={itemVariants}>
            <SectionTitle>Market Opportunity</SectionTitle>
            <Text>
              Eastern Asia represents a dynamic economic landscape with rapidly expanding 
              digital populations, exploding e-commerce growth, and significant digital 
              ad spend shifting towards performance-based models.
            </Text>
            <Text>
              Our unique approach combines traditional affiliate marketing with proprietary 
              content websites, creating multiple revenue streams and providing advertisers 
              with high-quality, pre-qualified traffic for e-commerce, SaaS platforms, 
              and digital service providers.
            </Text>
            <KeyPoints>
              <KeyPoint variants={itemVariants}>
                High internet penetration in urban areas
              </KeyPoint>
              <KeyPoint variants={itemVariants}>
                Mobile-first mentality across the region
              </KeyPoint>
              <KeyPoint variants={itemVariants}>
                Growing demand for trusted information sources
              </KeyPoint>
              <KeyPoint variants={itemVariants}>
                Untapped potential in digital service markets
              </KeyPoint>
            </KeyPoints>
          </Highlight>
        </ContentGrid>

        <StatsGrid variants={itemVariants}>
          <StatCard
            variants={cardVariants}
            whileHover={{ scale: 1.05, y: -5 }}
            transition={{ duration: 0.3 }}
          >
            <StatNumber>€12M</StatNumber>
            <StatLabel>Total Investment</StatLabel>
            <StatDescription>
              Comprehensive funding for technology, team, and market expansion
            </StatDescription>
          </StatCard>

          <StatCard
            variants={cardVariants}
            whileHover={{ scale: 1.05, y: -5 }}
            transition={{ duration: 0.3 }}
          >
            <StatNumber>€5M</StatNumber>
            <StatLabel>Content Investment</StatLabel>
            <StatDescription>
              Dedicated budget for building high-value proprietary websites
            </StatDescription>
          </StatCard>

          <StatCard
            variants={cardVariants}
            whileHover={{ scale: 1.05, y: -5 }}
            transition={{ duration: 0.3 }}
          >
            <StatNumber>8+</StatNumber>
            <StatLabel>Target Markets</StatLabel>
            <StatDescription>
              Strategic focus on high-growth Eastern Asian countries
            </StatDescription>
          </StatCard>

          <StatCard
            variants={cardVariants}
            whileHover={{ scale: 1.05, y: -5 }}
            transition={{ duration: 0.3 }}
          >
            <StatNumber>6</StatNumber>
            <StatLabel>Months to Launch</StatLabel>
            <StatDescription>
              Timeline for platform development and initial revenue streams
            </StatDescription>
          </StatCard>
        </StatsGrid>
      </motion.div>
    </SummaryContainer>
  );
};

export default ExecutiveSummary; 